/**
 * Formatting pipes. Every pipe is a pure function of (value, args, context).
 * They never throw for bad input: unformattable values pass through as text so
 * a report never fails to render because one field was unexpectedly empty.
 */

export interface FormatContext {
  locale: string;
  currency: string;
  timeZone?: string;
  /** Fixed "now" for deterministic output (tests, reproducible PDFs). */
  now?: Date;
}

export type PipeFn = (value: unknown, args: unknown[], ctx: FormatContext) => unknown;

export interface PipeDefinition {
  name: string;
  description: string;
  /** Output type used by the static type checker. "same" keeps the input type. */
  returns: "string" | "number" | "boolean" | "same" | "array";
  /** Argument descriptions shown in the editor. */
  args?: Array<{ name: string; type: "string" | "number" | "boolean" | "any"; optional?: boolean; description?: string }>;
  /** Input types this pipe is meant for (documentation and editor suggestions only). */
  accepts?: string[];
  fn: PipeFn;
}

const intlNumberCache = new Map<string, Intl.NumberFormat>();
const intlDateCache = new Map<string, Intl.DateTimeFormat>();

function numberFormat(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + JSON.stringify(opts);
  let f = intlNumberCache.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(locale, opts);
    } catch {
      f = new Intl.NumberFormat("en-GB", { ...opts, currency: opts.currency ?? "GBP" });
    }
    intlNumberCache.set(key, f);
  }
  return f;
}

function dateFormat(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = locale + JSON.stringify(opts);
  let f = intlDateCache.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(locale, opts);
    } catch {
      f = new Intl.DateTimeFormat("en-GB", opts);
    }
    intlDateCache.set(key, f);
  }
  return f;
}

export function toNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.replace(/[,\s]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "boolean") return value ? 1 : 0;
  return null;
}

export function toDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string" && value.trim()) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

export function toDisplayString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(toDisplayString).join(", ");
  return "";
}

/**
 * Date pattern tokens (a pragmatic subset of Unicode/date-fns tokens):
 * yyyy yy MMMM MMM MM M dd d EEEE EEE HH H hh h mm ss a
 */
export function formatDatePattern(date: Date, pattern: string, ctx: FormatContext): string {
  const parts = (opts: Intl.DateTimeFormatOptions) => {
    const o: Intl.DateTimeFormatOptions = { ...opts };
    if (ctx.timeZone) o.timeZone = ctx.timeZone;
    return dateFormat(ctx.locale, o).formatToParts(date);
  };
  const get = (opts: Intl.DateTimeFormatOptions, type: string) => parts(opts).find((p) => p.type === type)?.value ?? "";
  const pad = (s: string, n: number) => s.padStart(n, "0");
  const numeric = parts({ year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", hour12: false });
  const num = (type: string) => numeric.find((p) => p.type === type)?.value ?? "";
  const hour24 = Number(num("hour")) % 24;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return pattern.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|ss|a|'[^']*'/g, (tok) => {
    switch (tok) {
      case "yyyy":
        return num("year");
      case "yy":
        return num("year").slice(-2);
      case "MMMM":
        return get({ month: "long" }, "month");
      case "MMM":
        return get({ month: "short" }, "month");
      case "MM":
        return pad(num("month"), 2);
      case "M":
        return num("month");
      case "dd":
        return pad(num("day"), 2);
      case "d":
        return num("day");
      case "EEEE":
        return get({ weekday: "long" }, "weekday");
      case "EEE":
        return get({ weekday: "short" }, "weekday");
      case "HH":
        return pad(String(hour24), 2);
      case "H":
        return String(hour24);
      case "hh":
        return pad(String(hour12), 2);
      case "h":
        return String(hour12);
      case "mm":
        return pad(num("minute"), 2);
      case "ss":
        return pad(num("second"), 2);
      case "a":
        return hour24 < 12 ? "AM" : "PM";
      default:
        return tok.startsWith("'") ? tok.slice(1, -1) : tok;
    }
  });
}

const NAMED_DATE_STYLES: Record<string, Intl.DateTimeFormatOptions> = {
  short: { dateStyle: "short" },
  medium: { dateStyle: "medium" },
  long: { dateStyle: "long" },
  full: { dateStyle: "full" }
};

export function formatDate(value: unknown, pattern: string | undefined, ctx: FormatContext, withTime = false): string {
  const d = toDate(value);
  if (!d) return toDisplayString(value);
  if (pattern && NAMED_DATE_STYLES[pattern]) {
    const opts: Intl.DateTimeFormatOptions = { ...NAMED_DATE_STYLES[pattern] };
    if (withTime) opts.timeStyle = "short";
    if (ctx.timeZone) opts.timeZone = ctx.timeZone;
    return dateFormat(ctx.locale, opts).format(d);
  }
  if (pattern) return formatDatePattern(d, pattern, ctx);
  const opts: Intl.DateTimeFormatOptions = withTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" };
  if (ctx.timeZone) opts.timeZone = ctx.timeZone;
  return dateFormat(ctx.locale, opts).format(d);
}

export function formatCurrency(value: unknown, currency: string | undefined, decimals: number | undefined, ctx: FormatContext): string {
  const n = toNumber(value);
  if (n === null) return toDisplayString(value);
  const code = (currency && String(currency).toUpperCase()) || ctx.currency;
  const opts: Intl.NumberFormatOptions = { style: "currency", currency: code };
  if (decimals !== undefined) {
    opts.minimumFractionDigits = decimals;
    opts.maximumFractionDigits = decimals;
  }
  return numberFormat(ctx.locale, opts).format(n);
}

export function formatNumber(value: unknown, decimals: number | undefined, ctx: FormatContext): string {
  const n = toNumber(value);
  if (n === null) return toDisplayString(value);
  const opts: Intl.NumberFormatOptions = {};
  if (decimals !== undefined) {
    opts.minimumFractionDigits = decimals;
    opts.maximumFractionDigits = decimals;
  } else opts.maximumFractionDigits = 2;
  return numberFormat(ctx.locale, opts).format(n);
}

export function formatPercent(value: unknown, decimals: number | undefined, ctx: FormatContext, alreadyScaled = false): string {
  const n = toNumber(value);
  if (n === null) return toDisplayString(value);
  const opts: Intl.NumberFormatOptions = { style: "percent" };
  const d = decimals ?? 1;
  opts.minimumFractionDigits = d;
  opts.maximumFractionDigits = d;
  return numberFormat(ctx.locale, opts).format(alreadyScaled ? n / 100 : n);
}

function arg<T>(args: unknown[], i: number): T | undefined {
  return args[i] as T | undefined;
}

export const PIPES: Record<string, PipeDefinition> = {
  currency: {
    name: "currency",
    description: "Format as money",
    returns: "string",
    accepts: ["number", "currency"],
    args: [
      { name: "code", type: "string", optional: true, description: "ISO currency code, e.g. GBP" },
      { name: "decimals", type: "number", optional: true }
    ],
    fn: (v, a, ctx) => formatCurrency(v, arg<string>(a, 0), toNumber(a[1]) ?? undefined, ctx)
  },
  number: {
    name: "number",
    description: "Format with thousands separators",
    returns: "string",
    accepts: ["number"],
    args: [{ name: "decimals", type: "number", optional: true }],
    fn: (v, a, ctx) => formatNumber(v, toNumber(a[0]) ?? undefined, ctx)
  },
  percent: {
    name: "percent",
    description: "Format a fraction (0.25) as a percentage (25%)",
    returns: "string",
    accepts: ["number", "percent"],
    args: [
      { name: "decimals", type: "number", optional: true },
      { name: "scaled", type: "boolean", optional: true, description: "Set true when the value is already 0–100" }
    ],
    fn: (v, a, ctx) => formatPercent(v, toNumber(a[0]) ?? undefined, ctx, a[1] === true)
  },
  date: {
    name: "date",
    description: "Format a date",
    returns: "string",
    accepts: ["date", "datetime"],
    args: [{ name: "pattern", type: "string", optional: true, description: "e.g. dd MMM yyyy, or short/medium/long/full" }],
    fn: (v, a, ctx) => formatDate(v, arg<string>(a, 0), ctx, false)
  },
  datetime: {
    name: "datetime",
    description: "Format a date with time",
    returns: "string",
    accepts: ["date", "datetime"],
    args: [{ name: "pattern", type: "string", optional: true }],
    fn: (v, a, ctx) => formatDate(v, arg<string>(a, 0), ctx, true)
  },
  boolean: {
    name: "boolean",
    description: "Show a boolean as text",
    returns: "string",
    accepts: ["boolean"],
    args: [
      { name: "yes", type: "string", optional: true },
      { name: "no", type: "string", optional: true }
    ],
    fn: (v, a) => (v === true || v === "true" || v === 1 ? arg<string>(a, 0) ?? "Yes" : arg<string>(a, 1) ?? "No")
  },
  upper: { name: "upper", description: "UPPERCASE", returns: "string", fn: (v) => toDisplayString(v).toUpperCase() },
  lower: { name: "lower", description: "lowercase", returns: "string", fn: (v) => toDisplayString(v).toLowerCase() },
  title: {
    name: "title",
    description: "Title Case",
    returns: "string",
    fn: (v) => toDisplayString(v).replace(/\b\p{L}/gu, (c) => c.toUpperCase())
  },
  default: {
    name: "default",
    description: "Fallback when the value is empty",
    returns: "same",
    args: [{ name: "fallback", type: "any" }],
    fn: (v, a) => (isEmptyValue(v) ? a[0] : v)
  },
  join: {
    name: "join",
    description: "Join a list into text",
    returns: "string",
    accepts: ["array"],
    args: [{ name: "separator", type: "string", optional: true }],
    fn: (v, a) => (Array.isArray(v) ? v.map(toDisplayString).join(arg<string>(a, 0) ?? ", ") : toDisplayString(v))
  },
  truncate: {
    name: "truncate",
    description: "Limit length with an ellipsis",
    returns: "string",
    args: [{ name: "length", type: "number" }],
    fn: (v, a) => {
      const s = toDisplayString(v);
      const n = toNumber(a[0]) ?? 50;
      return s.length > n ? s.slice(0, Math.max(0, n - 1)).trimEnd() + "…" : s;
    }
  },
  round: {
    name: "round",
    description: "Round to N decimals",
    returns: "number",
    accepts: ["number"],
    args: [{ name: "decimals", type: "number", optional: true }],
    fn: (v, a) => {
      const n = toNumber(v);
      if (n === null) return v;
      const d = toNumber(a[0]) ?? 0;
      const m = 10 ** d;
      return Math.round(n * m) / m;
    }
  },
  abs: { name: "abs", description: "Absolute value", returns: "number", accepts: ["number"], fn: (v) => (toNumber(v) === null ? v : Math.abs(toNumber(v)!)) },
  pluralize: {
    name: "pluralize",
    description: "Choose singular/plural word by count",
    returns: "string",
    args: [
      { name: "singular", type: "string" },
      { name: "plural", type: "string" }
    ],
    fn: (v, a) => (toNumber(v) === 1 ? arg<string>(a, 0) ?? "" : arg<string>(a, 1) ?? `${arg<string>(a, 0) ?? ""}s`)
  },
  length: { name: "length", description: "Number of items or characters", returns: "number", fn: (v) => (Array.isArray(v) ? v.length : toDisplayString(v).length) },
  first: { name: "first", description: "First item", returns: "same", accepts: ["array"], fn: (v) => (Array.isArray(v) ? v[0] : v) },
  last: { name: "last", description: "Last item", returns: "same", accepts: ["array"], fn: (v) => (Array.isArray(v) ? v[v.length - 1] : v) },
  sortBy: {
    name: "sortBy",
    description: "Sort a list by a field",
    returns: "array",
    accepts: ["array"],
    args: [
      { name: "field", type: "string" },
      { name: "direction", type: "string", optional: true, description: "asc or desc" }
    ],
    fn: (v, a) => {
      if (!Array.isArray(v)) return v;
      const field = arg<string>(a, 0) ?? "";
      const dir = (arg<string>(a, 1) ?? "asc").toLowerCase() === "desc" ? -1 : 1;
      return [...v].sort((x, y) => compareValues(getField(x, field), getField(y, field)) * dir);
    }
  },
  take: {
    name: "take",
    description: "First N items",
    returns: "array",
    accepts: ["array"],
    args: [{ name: "count", type: "number" }],
    fn: (v, a) => (Array.isArray(v) ? v.slice(0, Math.max(0, toNumber(a[0]) ?? 0)) : v)
  },
  filter: {
    name: "filter",
    description: "Keep items whose field equals a value",
    returns: "array",
    accepts: ["array"],
    args: [
      { name: "field", type: "string" },
      { name: "value", type: "any" }
    ],
    fn: (v, a) => (Array.isArray(v) ? v.filter((x) => looseEquals(getField(x, arg<string>(a, 0) ?? ""), a[1])) : v)
  },
  pluck: {
    name: "pluck",
    description: "Extract one field from each item",
    returns: "array",
    accepts: ["array"],
    args: [{ name: "field", type: "string" }],
    fn: (v, a) => (Array.isArray(v) ? v.map((x) => getField(x, arg<string>(a, 0) ?? "")) : getField(v, arg<string>(a, 0) ?? ""))
  },
  initials: {
    name: "initials",
    description: "Initials of a name",
    returns: "string",
    fn: (v) =>
      toDisplayString(v)
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0]!.toUpperCase())
        .join("")
  }
};

export function getField(obj: unknown, path: string): unknown {
  if (obj === null || obj === undefined) return undefined;
  let cur: unknown = obj;
  for (const part of path.split(".")) {
    if (!part) continue;
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    if (!Object.prototype.hasOwnProperty.call(cur, part)) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

export function compareValues(a: unknown, b: unknown): number {
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na !== null && nb !== null && typeof a !== "string" && typeof b !== "string") return na - nb;
  const da = toDate(a);
  const db = toDate(b);
  if (da && db && (a instanceof Date || typeof a === "string") && (b instanceof Date || typeof b === "string") && /\d{4}-\d{2}/.test(String(a))) return da.getTime() - db.getTime();
  const sa = toDisplayString(a);
  const sb = toDisplayString(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

export function looseEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return (a ?? null) === (b ?? null);
  if (typeof a === "number" || typeof b === "number") {
    const na = toNumber(a);
    const nb = toNumber(b);
    if (na !== null && nb !== null) return na === nb;
  }
  if (typeof a === "boolean" || typeof b === "boolean") return String(a) === String(b);
  return toDisplayString(a) === toDisplayString(b);
}
